import type { DealData } from '../types';

/**
 * Maps buyer or co-buyer information from Redux state to DealData format
 * Handles null/undefined values by defaulting to empty strings
 */
export const mapBuyerInfoToDealData = (buyerInfo: {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zip?: string;
}): DealData['buyer'] => {
    return {
        firstName: buyerInfo.firstName || '',
        middleName: buyerInfo.middleName || '',
        lastName: buyerInfo.lastName || '',
        emailAddress: buyerInfo.email || '',
        phoneNumber: buyerInfo.phone || '',
        address: {
            street: buyerInfo.streetAddress || '',
            city: buyerInfo.city || '',
            state: buyerInfo.state || '',
            zip: buyerInfo.zip || ''
        }
    };
};

/**
 * Maps participant data from credit app raw deal structure to DealData format
 * Handles different address field naming (addressLine1 vs streetAddress)
 */
export const mapParticipantToDealData = (participant: {
    firstName?: string;
    middleName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    address?: {
        addressLine1?: string;
        city?: string;
        stateProvince?: string;
        postalCode?: string;
    };
}): DealData['buyer'] => {
    return {
        firstName: participant.firstName || '',
        middleName: participant.middleName || '',
        lastName: participant.lastName || '',
        emailAddress: participant.email || '',
        phoneNumber: participant.phone || '',
        address: {
            street: participant.address?.addressLine1 || '',
            city: participant.address?.city || '',
            state: participant.address?.stateProvince || '',
            zip: participant.address?.postalCode || ''
        }
    };
};

/**
 * Creates a complete DealData object from current Redux state
 * Includes buyer and optional co-buyer information
 */
export const mapCurrentDealData = (
    buyerInfo: Parameters<typeof mapBuyerInfoToDealData>[0],
    cobuyerInfo: Parameters<typeof mapBuyerInfoToDealData>[0] | null | undefined
): DealData => {
    return {
        buyer: mapBuyerInfoToDealData(buyerInfo),
        coBuyer: cobuyerInfo ? mapBuyerInfoToDealData(cobuyerInfo) : undefined
    };
};

/**
 * Creates a DealData object from credit app raw deal structure
 * Returns null if no credit app is selected
 */
export const mapToUpdatedDealData = (
    creditAppRawDeal: {
        dealStructure?: {
            participants?: {
                buyer?: Parameters<typeof mapParticipantToDealData>[0];
                coBuyer?: Parameters<typeof mapParticipantToDealData>[0];
            };
        };
    } | null
): DealData | null => {
    if (!creditAppRawDeal) {
        return null;
    }

    const buyer = creditAppRawDeal.dealStructure?.participants?.buyer || {};
    const coBuyer = creditAppRawDeal.dealStructure?.participants?.coBuyer;

    return {
        buyer: mapParticipantToDealData(buyer),
        coBuyer: coBuyer ? mapParticipantToDealData(coBuyer) : undefined
    };
};
